import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Clock, RefreshCw, Image, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPending() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [viewingProof, setViewingProof] = useState(null);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/payments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data pembayaran (unauthorized).");
      } else {
        toast.error("Gagal memuat data pembayaran");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (paymentId) => {
    try {
      setProcessingId(paymentId);
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/payments/${paymentId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Pembayaran dikonfirmasi!");
      
      // Open WhatsApp link in new tab
      if (response.data.wa_link) {
        window.open(response.data.wa_link, "_blank");
      }
      
      // Refresh list
      fetchPendingPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal konfirmasi pembayaran");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (paymentId) => {
    if (!window.confirm("Yakin ingin menolak pembayaran ini?")) return;
    
    try {
      setProcessingId(paymentId);
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/payments/${paymentId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Pembayaran ditolak");
      fetchPendingPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menolak pembayaran");
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout title="Pembayaran Pending">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="text-slate-600">
              {payments.length} pembayaran menunggu verifikasi
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchPendingPayments}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : payments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                Tidak Ada Pembayaran Pending
              </h3>
              <p className="text-slate-600">
                Semua pembayaran sudah diverifikasi
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="pending-payments-list">
            {payments.map((payment, index) => (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="hover:shadow-md transition-shadow" data-testid={`pending-payment-${payment.id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-800">{payment.nama}</h3>
                            <p className="text-sm text-slate-500">
                              No. Sambungan: {payment.no_samb} • Periode: {payment.periode_list}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span>WA: {payment.no_wa}</span>
                              <span>Submitted: {formatDate(payment.submitted_at)}</span>
                            </div>
                            {/* Payment Proof Button */}
                            {payment.payment_proof && (
                              <button
                                onClick={() => setViewingProof(payment.payment_proof)}
                                className="mt-2 inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
                              >
                                <Image className="w-4 h-4" />
                                Lihat Bukti Transfer
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Total Bayar</p>
                          <p className="text-xl font-heading font-bold text-sky-600">
                            {formatCurrency(payment.total_bayar)}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(payment.id)}
                            disabled={processingId === payment.id}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`reject-btn-${payment.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleConfirm(payment.id)}
                            disabled={processingId === payment.id}
                            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                            data-testid={`confirm-btn-${payment.id}`}
                          >
                            {processingId === payment.id ? (
                              <div className="spinner w-4 h-4" />
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4" />
                                Konfirmasi
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Payment Proof Modal */}
      {viewingProof && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingProof(null)}>
          <div className="relative max-w-2xl w-full bg-white rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-800">Bukti Pembayaran</h3>
              <button
                onClick={() => setViewingProof(null)}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              <img 
                src={viewingProof} 
                alt="Bukti Pembayaran" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
